import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import type { DataQualityData } from '../../types/cashCockpit';

interface Props {
    data: DataQualityData;
}

const severityPill: Record<string, string> = {
    Critical: 'bg-red-100 text-red-700 border-red-200',
    High: 'bg-red-100 text-red-700 border-red-200',
    Warning: 'bg-amber-100 text-amber-700 border-amber-200',
    Medium: 'bg-amber-100 text-amber-700 border-amber-200',
    OK: 'bg-green-100 text-green-700 border-green-200',
    Info: 'bg-gray-100 text-gray-700 border-gray-200',
    Low: 'bg-gray-100 text-gray-700 border-gray-200',
};

export default function DataQualityTab({ data }: Props) {
    const { summary, signals, opsLog } = data;

    return (
        <div className="space-y-4">
            {/* Summary pills */}
            <div className="grid grid-cols-4 gap-3">
                <SummaryTile label="Total signals" count={summary.total} tone="neutral" />
                <SummaryTile label="Critical" count={summary.critical} tone="critical" />
                <SummaryTile label="Warning" count={summary.warning} tone="warning" />
                <SummaryTile label="OK" count={summary.ok} tone="ok" />
            </div>

            {/* Signals */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <h3 className="text-sm font-semibold text-gray-900 px-4 py-3 border-b border-gray-200">
                    Live Signals
                </h3>
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600">
                        <tr>
                            <th className="px-3 py-2 text-left">Type</th>
                            <th className="px-3 py-2 text-left">Entity / Reference</th>
                            <th className="px-3 py-2 text-left">Detail</th>
                            <th className="px-3 py-2 text-left">Severity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {signals.length === 0 && (
                            <tr><td colSpan={4} className="text-center py-6 text-gray-500">All clear — no signals.</td></tr>
                        )}
                        {signals.map((s, i) => (
                            <tr key={i} className="border-t border-gray-100">
                                <td className="px-3 py-1.5 font-medium">{s.signalType}</td>
                                <td className="px-3 py-1.5">
                                    {s.companyName && (
                                        <span className="font-semibold mr-1">{s.companyName}</span>
                                    )}
                                    <span className="font-mono text-gray-500">({s.reference})</span>
                                </td>
                                <td className="px-3 py-1.5 text-gray-600">{s.detail}</td>
                                <td className="px-3 py-1.5">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${severityPill[s.severity] ?? severityPill.Info}`}>
                                        {s.severity}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Ops log */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <h3 className="text-sm font-semibold text-gray-900 px-4 py-3 border-b border-gray-200">
                    Ops Log
                </h3>
                <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600">
                        <tr>
                            <th className="px-3 py-2 text-left">Category</th>
                            <th className="px-3 py-2 text-left">Title</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-left">Severity</th>
                            <th className="px-3 py-2 text-left">Created / Resolved</th>
                        </tr>
                    </thead>
                    <tbody>
                        {opsLog.length === 0 && (
                            <tr><td colSpan={5} className="text-center py-6 text-gray-500">No ops log entries.</td></tr>
                        )}
                        {opsLog.map(e => (
                            <tr key={e.id} className="border-t border-gray-100 align-top">
                                <td className="px-3 py-1.5">{e.category}</td>
                                <td className="px-3 py-1.5">
                                    <div className="font-medium">{e.title}</div>
                                    {e.detail && <div className="text-gray-500 mt-0.5">{e.detail}</div>}
                                </td>
                                <td className="px-3 py-1.5">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                        e.status === 'Resolved'
                                            ? 'bg-green-100 text-green-700 border-green-200'
                                            : 'bg-gray-100 text-gray-700 border-gray-200'
                                    }`}>{e.status}</span>
                                </td>
                                <td className="px-3 py-1.5">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${severityPill[e.severity] ?? severityPill.Info}`}>
                                        {e.severity}
                                    </span>
                                </td>
                                <td className="px-3 py-1.5 text-gray-600">
                                    <div>{new Date(e.createdAt).toLocaleDateString('de-CH')}</div>
                                    {e.resolvedAt && <div className="text-green-600">Resolved: {new Date(e.resolvedAt).toLocaleDateString('de-CH')}</div>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SummaryTile({ label, count, tone }: { label: string; count: number; tone: 'neutral' | 'critical' | 'warning' | 'ok' }) {
    const bg = tone === 'critical' ? 'border-red-300 bg-red-50'
        : tone === 'warning' ? 'border-amber-300 bg-amber-50'
        : tone === 'ok' ? 'border-green-300 bg-green-50'
        : 'border-gray-200 bg-white';
    const color = tone === 'critical' ? 'text-red-700'
        : tone === 'warning' ? 'text-amber-700'
        : tone === 'ok' ? 'text-green-700'
        : 'text-gray-900';
    const Icon = tone === 'critical' ? XCircle
        : tone === 'warning' ? AlertTriangle
        : tone === 'ok' ? CheckCircle2
        : undefined;
    return (
        <div className={`border rounded-xl p-3 ${bg}`}>
            <div className="text-xs uppercase tracking-wide text-gray-600">{label}</div>
            <div className={`text-2xl font-semibold font-mono tabular-nums flex items-center gap-2 ${color}`}>
                {Icon && <Icon className="w-5 h-5" />}
                {count}
            </div>
        </div>
    );
}
